import { useState, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { getLanguageVarieties, getLanguageScripts, type LanguageVariety, type LanguageScript } from '@/app/[locale]/actions/language-varieties';
import { getUserSettings, updateUserSettings } from '@/app/[locale]/actions/settings';
import { type SelectedLanguage } from '@/components/translate/lang-dropdown';
import { useLocale, useTranslations } from 'next-intl';
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from '@/lib/utils';

interface LanguageVarietyScriptPickerProps {
  language: SelectedLanguage;
  selectedVarietyId?: string;
  selectedScriptId?: string;
  onVarietyChange: (varietyId: string | undefined) => void;
  onScriptChange: (scriptId: string | undefined) => void;
  className?: string;
}

export function LanguageVarietyScriptPicker({
  language,
  selectedVarietyId,
  selectedScriptId,
  onVarietyChange,
  onScriptChange,
  className = ""
}: LanguageVarietyScriptPickerProps) {
  const locale = useLocale();
  const t = useTranslations('languageVarietyScriptPicker');
  const queryClient = useQueryClient();
  const [hasInitialized, setHasInitialized] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [userHasSelected, setUserHasSelected] = useState(false);

  const varietySettingKey = `languageVariety`;
  const scriptSettingKey = `languageScript`;

  // Fetch all data in parallel using useQueries
  const [
    { data: varieties = [], isLoading: varietiesLoading, isError: varietiesError },
    { data: scripts = [], isLoading: scriptsLoading, isError: scriptsError },
    { data: varietySettings = {} },
    { data: scriptSettings = {} }
  ] = useQueries({
    queries: [
      {
        queryKey: ['language-varieties', language?.id, locale],
        queryFn: () => getLanguageVarieties(language!.id, locale),
        enabled: !!language?.default_variety_id,
        staleTime: 600000 // 10 minutes
      },
      {
        queryKey: ['language-scripts', language?.id, locale],
        queryFn: () => getLanguageScripts(language!.id, locale),
        enabled: !!language?.default_script_id,
        staleTime: 600000 // 10 minutes
      },
      {
        queryKey: ['settings', varietySettingKey],
        queryFn: () => getUserSettings(varietySettingKey),
        enabled: !!language?.default_variety_id,
        staleTime: 10000 // 10 secs
      },
      {
        queryKey: ['settings', scriptSettingKey],
        queryFn: () => getUserSettings(scriptSettingKey),
        enabled: !!language?.default_script_id,
        staleTime: 10000 // 10 secs
      }
    ]
  });

  // Auto-select defaults when data loads, considering user preferences
  useEffect(() => {
    if (!hasInitialized && language && !userHasSelected) {
      let shouldSetInitialized = true;

      // Check if we're still loading any required data
      if (language.default_variety_id && varietiesLoading) shouldSetInitialized = false;
      if (language.default_script_id && scriptsLoading) shouldSetInitialized = false;

      if (shouldSetInitialized) {
        // Handle variety selection
        if (language.default_variety_id && varieties.length > 0) {
          // If no variety is currently selected, auto-select based on user preference or default
          if (!selectedVarietyId) {
            // Check if user has a saved preference for this language
            const userPreferredVariety = varietySettings[language.id];
            const varietyToSelect = userPreferredVariety && varieties.find((v: LanguageVariety) => v.id === userPreferredVariety)
              ? userPreferredVariety
              : language.default_variety_id;

            onVarietyChange(varietyToSelect);
          }
          // If variety is already selected, validate it exists for this language
          else {
            const varietyExists = varieties.find((v: LanguageVariety) => v.id === selectedVarietyId);
            if (!varietyExists) {
              // Selected variety doesn't exist for this language, fall back to default
              onVarietyChange(language.default_variety_id);
            }
            // If it exists, keep the current selection (don't override)
          }
        } else if (!language.default_variety_id && selectedVarietyId) {
          // Clear variety if language changed and no longer has varieties
          onVarietyChange(undefined);
        }

        // Handle script selection
        if (language.default_script_id && scripts.length > 0) {
          // If no script is currently selected, auto-select based on user preference or default
          if (!selectedScriptId) {
            // Check if user has a saved preference for this language
            const userPreferredScript = scriptSettings[language.id];
            const scriptToSelect = userPreferredScript && scripts.find((s: LanguageScript) => s.id === userPreferredScript)
              ? userPreferredScript
              : language.default_script_id;

            onScriptChange(scriptToSelect);
          }
          // If script is already selected, validate it exists for this language
          else {
            const scriptExists = scripts.find((s: LanguageScript) => s.id === selectedScriptId);
            if (!scriptExists) {
              // Selected script doesn't exist for this language, fall back to default
              onScriptChange(language.default_script_id);
            }
            // If it exists, keep the current selection (don't override)
          }
        } else if (!language.default_script_id && selectedScriptId) {
          // Clear script if language changed and no longer has scripts
          onScriptChange(undefined);
        }

        setHasInitialized(true);
      }
    }
  }, [
    language,
    varieties,
    scripts,
    varietySettings,
    scriptSettings,
    selectedVarietyId,
    selectedScriptId,
    onVarietyChange,
    onScriptChange,
    hasInitialized,
    varietiesLoading,
    scriptsLoading,
    userHasSelected
  ]);

  // Reset initialization when language changes
  useEffect(() => {
    setHasInitialized(false);
    setUserHasSelected(false);
  }, [language?.id]);

  // Helper function to handle variety selection with persistence
  const handleVarietySelect = async (varietyId: string) => {
    setUserHasSelected(true);
    onVarietyChange(varietyId);
    setIsPopoverOpen(false);

    // Save user preference
    if (language?.id) {
      await updateUserSettings(varietySettingKey, { [language.id]: varietyId });
      // Invalidate the settings cache to reflect the change immediately
      queryClient.invalidateQueries({ queryKey: ['settings', varietySettingKey] });
    }
  };

  // Helper function to handle script selection with persistence
  const handleScriptSelect = async (scriptId: string) => {
    setUserHasSelected(true);
    onScriptChange(scriptId);
    setIsPopoverOpen(false);

    // Save user preference
    if (language?.id) {
      await updateUserSettings(scriptSettingKey, { [language.id]: scriptId });
      // Invalidate the settings cache to reflect the change immediately
      queryClient.invalidateQueries({ queryKey: ['settings', scriptSettingKey] });
    }
  };

  // Helper function to handle variety selection via dropdown with persistence
  const handleVarietyDropdownChange = async (varietyId: string | undefined) => {
    setUserHasSelected(true);
    onVarietyChange(varietyId);

    // Save user preference
    if (language?.id && varietyId) {
      await updateUserSettings(varietySettingKey, { [language.id]: varietyId });
      // Invalidate the settings cache to reflect the change immediately
      queryClient.invalidateQueries({ queryKey: ['settings', varietySettingKey] });
    }
  };

  // Helper function to handle script selection via dropdown with persistence
  const handleScriptDropdownChange = async (scriptId: string | undefined) => {
    setUserHasSelected(true);
    onScriptChange(scriptId);

    // Save user preference
    if (language?.id && scriptId) {
      await updateUserSettings(scriptSettingKey, { [language.id]: scriptId });
      // Invalidate the settings cache to reflect the change immediately
      queryClient.invalidateQueries({ queryKey: ['settings', scriptSettingKey] });
    }
  };

  // Don't render anything if language doesn't have varieties or scripts to choose from
  if (!language || (!language.default_variety_id && !language.default_script_id)) {
    return null;
  }

  // Show loading state until we have all required data
  const isLoadingRequiredData =
    (language.default_variety_id && (varietiesLoading || varieties.length === 0)) ||
    (language.default_script_id && (scriptsLoading || scripts.length === 0));

  if (isLoadingRequiredData && !hasInitialized) {
    return (
      <div className={cn('flex gap-2', className)}>
        <div className="hidden md:flex gap-2 min-w-36">
          {language.default_variety_id && <Skeleton className="h-9 flex-1" />}
          {language.default_script_id && <Skeleton className="h-9 flex-1" />}
        </div>
        <div className="md:hidden flex-1">
          <Skeleton className="h-9 w-full" />
        </div>
      </div>
    );
  }

  // Get selected variety and script names for display
  // const selectedVariety = varieties.find((v: LanguageVariety) => v.id === selectedVarietyId);
  // const selectedScript = scripts.find((s: LanguageScript) => s.id === selectedScriptId);

  return (
    <div className={cn('flex gap-2', className)}>
      {/* Desktop View - Show dropdowns side by side on md and larger screens */}
      <div className="hidden md:flex gap-2">
        {/* Variety Picker */}
        {language.default_variety_id && (
          <div className="flex-1 min-w-15">
            {varietiesLoading ? (
              <Skeleton className="h-9 w-full" />
            ) : varietiesError ? (
              <div className="text-destructive">{t('errorLoadingVarieties')}</div>
            ) : (
              <Select
                value={selectedVarietyId || ""}
                onValueChange={(value) => handleVarietyDropdownChange(value || undefined)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('varietyPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {varieties.map((variety: LanguageVariety) => (
                    <SelectItem key={variety.id} value={variety.id} >
                      {variety.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        )}

        {/* Script Picker */}
        {language.default_script_id && (
          <div className="flex-1 min-w-15">
            {scriptsLoading ? (
              <Skeleton className="h-9 w-full" />
            ) : scriptsError ? (
              <div className="text-destructive">{t('errorLoadingScripts')}</div>
            ) : (
              <Select
                value={selectedScriptId || ""}
                onValueChange={(value) => handleScriptDropdownChange(value || undefined)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('scriptPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {scripts.map((script: LanguageScript) => (
                    <SelectItem key={script.id} value={script.id}>
                      {script.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        )}
      </div>

      {/* Mobile View - Show popover with side-by-side menus on smaller screens */}
      <div className="md:hidden flex-1">
        <TooltipProvider>
          <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-between px-3">
                <Tooltip >
                  <TooltipTrigger asChild>
                    <span className="text-lg leading-none">⋯</span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t('chooseVarietyAndScript')}</p>
                  </TooltipContent>
                </Tooltip>
              </Button>
            </PopoverTrigger>

            <PopoverContent className="w-[400px] max-w-[95vw] p-0" align="end">
              <div className="flex">
                {/* Variety Selection */}
                {language.default_variety_id && (
                  <div className="flex-1 border-r">
                    <div className="p-3 border-b">
                      <h4 className="text-sm font-medium">{t('variety')}</h4>
                    </div>
                    <div className="max-h-[calc(100vh-200px)] overflow-y-auto">
                      {varietiesLoading ? (
                        <div className="p-3">
                          <Skeleton className="h-6 w-full mb-2" />
                          <Skeleton className="h-6 w-3/4" />
                        </div>
                      ) : varietiesError ? (
                        <div className="p-3 text-destructive">{t('errorLoadingVarieties')}</div>
                      ) : (
                        <div className="space-y-1 p-2">
                          {varieties.map((variety: LanguageVariety) => (
                            <button
                              key={variety.id}
                              onClick={() => handleVarietySelect(variety.id)}
                              className={cn(
                                "w-full text-left p-2 rounded hover:bg-accent transition-colors",
                                selectedVarietyId === variety.id && "bg-accent font-medium"
                              )}
                            >
                              {variety.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Script Selection */}
                {language.default_script_id && (
                  <div className="flex-1">
                    <div className="p-3 border-b">
                      <h4 className="text-sm font-medium">{t('script')}</h4>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {scriptsLoading ? (
                        <div className="p-3">
                          <Skeleton className="h-6 w-full mb-2" />
                          <Skeleton className="h-6 w-3/4" />
                        </div>
                      ) : scriptsError ? (
                        <div className="p-3 text-destructive">{t('errorLoadingScripts')}</div>
                      ) : (
                        <div className="space-y-1 p-2">
                          {scripts.map((script: LanguageScript) => (
                            <button
                              key={script.id}
                              onClick={() => handleScriptSelect(script.id)}
                              className={cn(
                                "w-full text-left p-2 rounded hover:bg-accent transition-colors",
                                selectedScriptId === script.id && "bg-accent font-medium"
                              )}
                            >
                              {script.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>
        </TooltipProvider>
      </div>
    </div>
  );
}
